/**
 * hybrid_player_adapter.ts
 *
 * IPlayerAdapter implementation for the JTT1078 relay WebSocket stream.
 *
 * Wire protocol (matches relay nalu_framer.go / audio_framer.go):
 *   MSG_VIDEO_INIT  (0x01): codec config + SPS/PPS — sent once, re-sent to late joiners
 *   MSG_VIDEO_FRAME (0x02): one reassembled frame (NALU Annex B) + pts
 *   MSG_AUDIO_INIT  (0x03): G.711 codec + sample rate — re-sent every ~20 s and
 *                           replayed to every late subscriber, so handle it idempotently
 *   MSG_AUDIO_FRAME (0x04): one complete G.711 frame + pts (never fragmented)
 *
 * Unknown tags must stay inert: the dispatcher below is an `if / else if` chain
 * with no `else`, and that property is what made it safe to ship 0x03/0x04 to
 * clients that predate them. Preserve it when adding branches.
 *
 * Decoder selection (per-device, no UA sniffing):
 *   VideoDecoder.isConfigSupported() → 'webcodecs' (hardware GPU)
 *                                    → 'wasm'      (h265web.js Wasm, H.265 fallback)
 *
 * Test order:
 *   1. H.264 + WebCodecsDecoder  — always this path on modern browsers
 *   2. H.265 + WebCodecsDecoder  — Chrome/Edge/Safari Apple Silicon
 *   3. H.265 + WasmDecoder       — Firefox, Safari Intel, old Android
 */

import type {
  IPlayerAdapter,
  PlayerAdapterStats,
  PlayerFrameQualityChange,
  PlayerAudioStateChange,
} from 'livekit_web';
import { AudioSink } from './audio_player';
import { g711CodecName } from './g711_decoder';
import { detectDecoderType, type DecoderType } from './detector';
import { WebCodecsDecoder } from './webcodecs_decoder';
import { WasmDecoder } from './wasm_decoder';
import { VisualArtifactProbe, isH265DebugEnabled } from './h265_debug';
import { FrameQualityGate } from './frame_quality_gate';
import { JitterBuffer } from './jitter_buffer';

// ── Wire format constants (must match relay nalu_framer.py) ─────────────────

const MSG_VIDEO_INIT = 0x01;
const MSG_VIDEO_FRAME = 0x02;
const MSG_AUDIO_INIT = 0x03;
const MSG_AUDIO_FRAME = 0x04;
const WASM_DECODER_LIFECYCLE_BUDGET = 60;
const WASM_MEMORY_REFRESH_EVENT = 'vgps-wasm-memory-refresh';
const WASM_MEMORY_REFRESH_COUNT_KEY = 'vgps-wasm-decoder-lifecycle-count';
const WASM_MEMORY_REFRESH_PENDING_KEY = 'vgps-wasm-memory-refresh-pending';

/** A backward PTS jump larger than this (µs) is a stream rebase (camera restart
 *  / PTS base change), not a stale frame — the monotonic guard follows it.
 *  Matches the jitter buffer's DISCONTINUITY_MS (3 s). */
const BACKWARD_REBASE_US = 3_000_000;

/** Audio PTS discontinuity threshold (µs). Mirrors the video policy: a device
 *  reboot rebases PTS toward 0, i.e. a guaranteed multi-hour backward jump, and
 *  without this the audio ring wedges exactly as the video queue would. */
const AUDIO_DISCONTINUITY_US = 3_000_000;

/** Never synthesise more than this much silence across one measured PTS gap. */
const AUDIO_MAX_SILENCE_US = 1_000_000;

const CODEC_H264 = 0;
const CODEC_H265 = 1;

// ── VIDEO_INIT message parser ────────────────────────────────────────────────

interface VideoInitMsg {
  codec: number;
  width: number;
  height: number;
  fps: number;
  sps: Uint8Array;
  pps: Uint8Array;
}

function parseVideoInit(buf: ArrayBuffer): VideoInitMsg {
  const v = new DataView(buf);
  // Byte 0: type (0x01)
  // Byte 1: codec
  // Bytes 2-3: width (uint16 BE)
  // Bytes 4-5: height (uint16 BE)
  // Byte 6: fps
  let offset = 7;
  const spsLen = v.getUint16(offset, false); offset += 2;
  const sps = new Uint8Array(buf, offset, spsLen); offset += spsLen;
  const ppsLen = v.getUint16(offset, false); offset += 2;
  const pps = new Uint8Array(buf, offset, ppsLen);
  return {
    codec: v.getUint8(1),
    width: v.getUint16(2, false),
    height: v.getUint16(4, false),
    fps: v.getUint8(6),
    sps,
    pps,
  };
}

// ── AUDIO_INIT message parser ────────────────────────────────────────────────

interface AudioInitMsg {
  /** 0 = PCMA / G.711 A-law, 1 = PCMU / G.711 µ-law. */
  codec: number;
  sampleRate: number;
  channels: number;
  /** Bits per sample of the DECODED PCM (16), not of the 8-bit compressed wire. */
  bitsPerSample: number;
}

/**
 * Parse the fixed 8-byte MSG_AUDIO_INIT.
 *
 *   0  u8   tag (0x03)
 *   1  u8   codec (0 = A-law, 1 = µ-law)
 *   2  u32  sample rate, Hz — BIG-ENDIAN
 *   6  u8   channel count
 *   7  u8   bits per sample of the decoded PCM
 *
 * Written from the wire table in HANDOFF-web-player-audio.md §1, deliberately
 * NOT by copying `parseVideoInit` above it. That function reads width/height
 * big-endian while the relay writes them little-endian
 * (`nalu_framer.go:19-23`) — a real defect that stays invisible only because
 * the sole caller passes zeros (`tcp_server.go:442`), and zero is
 * endian-agnostic. MSG_AUDIO_INIT genuinely is big-endian
 * (`audio_framer.go:47`, `binary.BigEndian.PutUint32`) and carries non-zero
 * values from the first message, so an endianness slip here fails loudly
 * (8000 misread little-endian is 0x401F0000, about 1.07 GHz).
 */
function parseAudioInit(buf: ArrayBuffer): AudioInitMsg | null {
  if (buf.byteLength < 8) return null;
  const v = new DataView(buf);
  return {
    codec: v.getUint8(1),
    sampleRate: v.getUint32(2, false),
    channels: v.getUint8(6),
    bitsPerSample: v.getUint8(7),
  };
}

/**
 * Return every H.265 nal_unit_type present in an Annex B buffer (debug probe).
 * Used to verify that IDR ACUs carry VPS(32)+SPS(33)+PPS(34) before the slice.
 */
function _listAnnexBNalTypes(data: Uint8Array): number[] {
  const types: number[] = [];
  const n = data.length;
  let i = 0;
  while (i < n - 3) {
    let sc = -1, scLen = 0;
    for (let j = i; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0) {
        if (data[j + 2] === 1) { sc = j; scLen = 3; break; }
        if (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1) { sc = j; scLen = 4; break; }
      }
    }
    if (sc === -1) break;
    const off = sc + scLen;
    if (off < n) types.push((data[off] >> 1) & 0x3f);
    i = off + 1;
  }
  return types;
}

/**
 * Build the correct AVC codec string from a raw SPS NAL unit (with Annex B start code).
 * The SPS header contains profile_idc / constraint_set_flags / level_idc which together
 * form the codec string "avc1.PPCCLL" required by VideoDecoder.isConfigSupported().
 * Using the exact profile from the encoder avoids "Decoding error" when the encoder
 * outputs High Profile but the decoder is configured for Baseline.
 */
function buildH264CodecString(sps: Uint8Array): string {
  // Skip Annex B start code: [00 00 00 01] (4 bytes) or [00 00 01] (3 bytes)
  let offset = 0;
  if (sps.length >= 4 && sps[0] === 0 && sps[1] === 0 && sps[2] === 0 && sps[3] === 1) {
    offset = 4;
  } else if (sps.length >= 3 && sps[0] === 0 && sps[1] === 0 && sps[2] === 1) {
    offset = 3;
  }
  // offset → NAL type byte (0x67), offset+1 → profile_idc, offset+2 → constraints, offset+3 → level_idc
  if (sps.length < offset + 4) return 'avc1.42E01E'; // fallback: Baseline L3.0
  const profile = sps[offset + 1];
  const constraint = sps[offset + 2];
  const level = sps[offset + 3];
  return `avc1.${profile.toString(16).padStart(2, '0')}${constraint.toString(16).padStart(2, '0')}${level.toString(16).padStart(2, '0')}`;
}

type H265SampleEntry = 'hev1' | 'hvc1';

interface H265NalUnit {
  type: number;
  data: Uint8Array;
}

interface H265SpsConfig {
  generalProfileSpace: number;
  generalTierFlag: number;
  generalProfileIdc: number;
  generalProfileCompatibilityFlags: Uint8Array;
  generalConstraintIndicatorFlags: Uint8Array;
  generalLevelIdc: number;
  chromaFormatIdc: number;
  bitDepthLumaMinus8: number;
  bitDepthChromaMinus8: number;
  numTemporalLayers: number;
  temporalIdNested: boolean;
  picWidthInLumaSamples: number;
  picHeightInLumaSamples: number;
  /** Conformance-cropped display dimensions (the real picture size). */
  displayWidth: number;
  displayHeight: number;
}

interface H265DecoderConfigData {
  codecString: string;
  description?: Uint8Array;
  frameArea: number;
  /** Display dimensions parsed from the SPS (relay sends 0 in VIDEO_INIT). */
  width?: number;
  height?: number;
}

function _findH265NalUnit(sources: Uint8Array[], type: number): Uint8Array | undefined {
  for (const source of sources) {
    const unit = _splitAnnexBNalUnits(source).find((entry) => entry.type === type)?.data;
    if (unit) return unit;
  }
  return undefined;
}

function _equalUint8Arrays(left: Uint8Array | null, right: Uint8Array): boolean {
  if (!left || left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

function _splitAnnexBNalUnits(data: Uint8Array): H265NalUnit[] {
  const units: H265NalUnit[] = [];
  const n = data.length;
  let i = 0;
  let foundStartCode = false;

  while (i < n - 2) {
    let sc = -1;
    let scLen = 0;
    for (let j = i; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0) {
        if (data[j + 2] === 1) { sc = j; scLen = 3; break; }
        if (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1) { sc = j; scLen = 4; break; }
      }
    }
    if (sc === -1) break;

    foundStartCode = true;
    const nalStart = sc + scLen;
    if (nalStart >= n) break;

    let nalEnd = n;
    for (let j = nalStart + 1; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0 &&
          (data[j + 2] === 1 || (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1))) {
        nalEnd = j;
        break;
      }
    }

    units.push({
      type: (data[nalStart] >> 1) & 0x3f,
      data: data.subarray(nalStart, nalEnd),
    });
    i = nalEnd;
  }

  if (!foundStartCode && data.length > 0) {
    units.push({
      type: (data[0] >> 1) & 0x3f,
      data,
    });
  }

  return units;
}

function _removeEmulationPreventionBytes(data: Uint8Array): Uint8Array {
  const rbsp = new Uint8Array(data.length);
  let offset = 0;
  for (let i = 0; i < data.length; i++) {
    if (i >= 2 && data[i] === 0x03 && data[i - 1] === 0 && data[i - 2] === 0) {
      continue;
    }
    rbsp[offset++] = data[i];
  }
  return rbsp.subarray(0, offset);
}

class _BitReader {
  private bitPos = 0;

  constructor(private readonly data: Uint8Array) {}

  readBits(count: number): number {
    let value = 0;
    for (let i = 0; i < count; i++) {
      if (this.bitPos >= this.data.length * 8) throw new Error('Unexpected end of RBSP');
      const byte = this.data[this.bitPos >> 3];
      const bit = (byte >> (7 - (this.bitPos & 7))) & 1;
      value = (value << 1) | bit;
      this.bitPos++;
    }
    return value;
  }

  readBool(): boolean {
    return this.readBits(1) === 1;
  }

  skipBits(count: number): void {
    this.bitPos += count;
    if (this.bitPos > this.data.length * 8) throw new Error('Unexpected end of RBSP');
  }

  readUEV(): number {
    let leadingZeros = 0;
    while (this.readBits(1) === 0) leadingZeros++;
    if (leadingZeros === 0) return 0;
    return ((1 << leadingZeros) - 1) + this.readBits(leadingZeros);
  }
}

function _parseH265SpsConfig(spsNal: Uint8Array): H265SpsConfig | null {
  if (spsNal.length < 3) return null;

  try {
    const rbsp = _removeEmulationPreventionBytes(spsNal.subarray(2));
    const reader = new _BitReader(rbsp);

    reader.readBits(4); // sps_video_parameter_set_id
    const maxSubLayersMinus1 = reader.readBits(3);
    const temporalIdNested = reader.readBool();

    const generalProfileSpace = reader.readBits(2);
    const generalTierFlag = reader.readBits(1);
    const generalProfileIdc = reader.readBits(5);

    const generalProfileCompatibilityFlags = new Uint8Array(4);
    for (let i = 0; i < 4; i++) generalProfileCompatibilityFlags[i] = reader.readBits(8);

    const generalConstraintIndicatorFlags = new Uint8Array(6);
    for (let i = 0; i < 6; i++) generalConstraintIndicatorFlags[i] = reader.readBits(8);

    const generalLevelIdc = reader.readBits(8);

    const subLayerProfilePresentFlags: boolean[] = [];
    const subLayerLevelPresentFlags: boolean[] = [];
    for (let i = 0; i < maxSubLayersMinus1; i++) {
      subLayerProfilePresentFlags.push(reader.readBool());
      subLayerLevelPresentFlags.push(reader.readBool());
    }
    if (maxSubLayersMinus1 > 0) {
      for (let i = maxSubLayersMinus1; i < 8; i++) reader.skipBits(2);
    }
    for (let i = 0; i < maxSubLayersMinus1; i++) {
      if (subLayerProfilePresentFlags[i]) reader.skipBits(88);
      if (subLayerLevelPresentFlags[i]) reader.skipBits(8);
    }

    reader.readUEV(); // sps_seq_parameter_set_id
    const chromaFormatIdc = reader.readUEV();
    if (chromaFormatIdc === 3) reader.skipBits(1); // separate_colour_plane_flag
    const picWidthInLumaSamples = reader.readUEV();
    const picHeightInLumaSamples = reader.readUEV();
    // Conformance window crop — the encoder pads coded dimensions up to a CTB
    // multiple (e.g. 1080 → coded 1088) and crops back for display. Apply it so
    // the rendered aspect ratio matches the real picture.
    let confWinLeft = 0;
    let confWinRight = 0;
    let confWinTop = 0;
    let confWinBottom = 0;
    if (reader.readBool()) {
      confWinLeft = reader.readUEV();
      confWinRight = reader.readUEV();
      confWinTop = reader.readUEV();
      confWinBottom = reader.readUEV();
    }
    const subWidthC = chromaFormatIdc === 1 || chromaFormatIdc === 2 ? 2 : 1;
    const subHeightC = chromaFormatIdc === 1 ? 2 : 1;
    const displayWidth = picWidthInLumaSamples - subWidthC * (confWinLeft + confWinRight);
    const displayHeight = picHeightInLumaSamples - subHeightC * (confWinTop + confWinBottom);
    const bitDepthLumaMinus8 = reader.readUEV();
    const bitDepthChromaMinus8 = reader.readUEV();

    return {
      generalProfileSpace,
      generalTierFlag,
      generalProfileIdc,
      generalProfileCompatibilityFlags,
      generalConstraintIndicatorFlags,
      generalLevelIdc,
      chromaFormatIdc,
      bitDepthLumaMinus8,
      bitDepthChromaMinus8,
      numTemporalLayers: maxSubLayersMinus1 + 1,
      temporalIdNested,
      picWidthInLumaSamples,
      picHeightInLumaSamples,
      displayWidth,
      displayHeight,
    };
  } catch {
    return null;
  }
}

function _formatH265ConstraintPart(bytes: Uint8Array): string {
  let lastNonZero = -1;
  for (let i = bytes.length - 1; i >= 0; i--) {
    if (bytes[i] !== 0) {
      lastNonZero = i;
      break;
    }
  }
  if (lastNonZero < 0) return 'B0';
  return Array.from(bytes.subarray(0, lastNonZero + 1))
    .map((value) => value.toString(16).toUpperCase().padStart(2, '0'))
    .join('.');
}

function _buildH265CodecStringFromConfig(config: H265SpsConfig, sampleEntry: H265SampleEntry): string {
  const profileSpace = config.generalProfileSpace > 0
    ? String.fromCharCode(0x40 + config.generalProfileSpace)
    : '';
  const compatibility =
    ((config.generalProfileCompatibilityFlags[0] << 24) |
      (config.generalProfileCompatibilityFlags[1] << 16) |
      (config.generalProfileCompatibilityFlags[2] << 8) |
      config.generalProfileCompatibilityFlags[3]) >>> 0;
  const tier = config.generalTierFlag ? 'H' : 'L';
  const constraints = _formatH265ConstraintPart(config.generalConstraintIndicatorFlags);

  return `${sampleEntry}.${profileSpace}${config.generalProfileIdc}.${compatibility.toString(16).toUpperCase() || '0'}.${tier}${config.generalLevelIdc}.${constraints}`;
}

/**
 * Build an HEVC codec string from a raw SPS NAL unit (Annex B, H.265 SPS_NUT=33).
 *
 * The relay packs VPS+SPS together in the `sps` field of VIDEO_INIT, so this
 * scans the buffer to find SPS_NUT (type 33) and derives the sample entry.
 */
function buildH265CodecString(spsData: Uint8Array, sampleEntry: H265SampleEntry = 'hev1'): string {
  const sps = _splitAnnexBNalUnits(spsData).find((unit) => unit.type === 33)?.data;
  const config = sps ? _parseH265SpsConfig(sps) : null;
  return config ? _buildH265CodecStringFromConfig(config, sampleEntry) : `${sampleEntry}.1.6.L93.B0`;
}

function _buildHevcDecoderConfigurationRecord(
  vps: Uint8Array,
  sps: Uint8Array,
  pps: Uint8Array,
  config: H265SpsConfig,
): Uint8Array {
  const totalSize = 23 + (5 + vps.length) + (5 + sps.length) + (5 + pps.length);
  const data = new Uint8Array(totalSize);
  data[0] = 1;
  data[1] =
    ((config.generalProfileSpace & 0x03) << 6) |
    ((config.generalTierFlag & 0x01) << 5) |
    (config.generalProfileIdc & 0x1f);
  data.set(config.generalProfileCompatibilityFlags, 2);
  data.set(config.generalConstraintIndicatorFlags, 6);
  data[12] = config.generalLevelIdc;
  data[13] = 0xf0;
  data[14] = 0;
  data[15] = 0xfc;
  data[16] = 0xfc | (config.chromaFormatIdc & 0x03);
  data[17] = 0xf8 | (config.bitDepthLumaMinus8 & 0x07);
  data[18] = 0xf8 | (config.bitDepthChromaMinus8 & 0x07);
  data[19] = 0;
  data[20] = 0;
  data[21] =
    ((0 & 0x03) << 6) |
    ((config.numTemporalLayers & 0x07) << 3) |
    ((config.temporalIdNested ? 1 : 0) << 2) |
    0x03;
  data[22] = 0x03;

  let offset = 23;
  for (const unit of [
    { type: 32, data: vps },
    { type: 33, data: sps },
    { type: 34, data: pps },
  ]) {
    data[offset] = 0x80 | unit.type;
    data[offset + 1] = 0;
    data[offset + 2] = 1;
    data[offset + 3] = (unit.data.length >> 8) & 0xff;
    data[offset + 4] = unit.data.length & 0xff;
    data.set(unit.data, offset + 5);
    offset += 5 + unit.data.length;
  }

  return data;
}

function buildH265DecoderConfig(spsData: Uint8Array, ppsData: Uint8Array): H265DecoderConfigData {
  return buildH265DecoderConfigFromSources(spsData, ppsData);
}

function buildH265DecoderConfigFromSources(...sources: Uint8Array[]): H265DecoderConfigData {
  const usableSources = sources.filter((source) => source.length > 0);
  const primarySource = usableSources[0] ?? new Uint8Array(0);
  const vps = _findH265NalUnit(usableSources, 32);
  const sps = _findH265NalUnit(usableSources, 33);
  const pps = _findH265NalUnit(usableSources, 34);

  if (!sps) {
    return { codecString: buildH265CodecString(primarySource, 'hev1'), frameArea: 0 };
  }

  const config = _parseH265SpsConfig(sps);
  if (!config) {
    return { codecString: buildH265CodecString(primarySource, 'hev1'), frameArea: 0 };
  }

  const codecString = _buildH265CodecStringFromConfig(config, vps && pps ? 'hvc1' : 'hev1');
  if (!vps || !pps) {
    return {
      codecString,
      frameArea: config.picWidthInLumaSamples * config.picHeightInLumaSamples,
      width: config.displayWidth,
      height: config.displayHeight,
    };
  }

  return {
    codecString,
    description: _buildHevcDecoderConfigurationRecord(vps, sps, pps, config),
    frameArea: config.picWidthInLumaSamples * config.picHeightInLumaSamples,
    width: config.displayWidth,
    height: config.displayHeight,
  };
}

// ── Adapter ──────────────────────────────────────────────────────────────────

/** Runtime-tunable knobs for the JT/T1078 player path (wire from app env). */
export interface HybridPlayerAdapterOptions {
  /** Initial jitter-buffer depth (ms). Default 50. */
  jitterBufferInitialMs?: number;
  /** Max jitter-buffer depth the adaptive target may grow to (ms). Default 200. */
  jitterBufferMaxMs?: number;
  /**
   * Ms orphaned (no re-anchor keyframe) before the gate stops dropping and
   * admits P-frames onto the stale reference. 0 = never freeze. Default 800.
   */
  recoveringFallbackMs?: number;
  /**
   * Initial audio cushion depth (ms). Default 300 — the measured equilibrium of
   * a real production path, see TARGET_INITIAL_MS. A hint rather than a
   * setting: the ring adapts from there, growing on an observed delivery gap or
   * an underrun and decaying when quiet, clamped to [120, 1200]. The page also
   * carries whatever depth it last needed across quality switches, so this only
   * decides where a fresh page begins.
   *
   * Raising it is close to free and usually improves A/V sync rather than
   * hurting it: video is buffered 500–1240 ms and played on a variable-rate
   * playhead, so it already sits 0.6–1.5 s behind while free-running audio
   * lands near 200 ms. Audio leads, so a deeper audio cushion closes the gap.
   */
  audioBufferTargetMs?: number;
}

function emitLiveTiming(stage: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('vgps-live-timing', { detail: { stage, atMs: Date.now() } }));
}

export class HybridPlayerAdapter implements IPlayerAdapter {
  private static warmedWasm: { baseUrl: string; promise: Promise<void> } | null = null;

  private static noteWasmDecoderDisposed(): void {
    if (typeof window === 'undefined') return;
    try {
      if (window.sessionStorage.getItem(WASM_MEMORY_REFRESH_PENDING_KEY) === '1') return;
      const count = Number(window.sessionStorage.getItem(WASM_MEMORY_REFRESH_COUNT_KEY) ?? '0') + 1;
      if (count < WASM_DECODER_LIFECYCLE_BUDGET) {
        window.sessionStorage.setItem(WASM_MEMORY_REFRESH_COUNT_KEY, String(count));
        return;
      }
      window.sessionStorage.removeItem(WASM_MEMORY_REFRESH_COUNT_KEY);
      window.sessionStorage.setItem(WASM_MEMORY_REFRESH_PENDING_KEY, '1');
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent(WASM_MEMORY_REFRESH_EVENT));
      }, 0);
      // The CMS listener displays a toast before this fallback reload. Keep the
      // safety valve independent from any particular host application.
      window.setTimeout(() => {
        window.sessionStorage.removeItem(WASM_MEMORY_REFRESH_PENDING_KEY);
        window.location.reload();
      }, 1_500);
    } catch {
      // Storage can be disabled; normal teardown remains safe in that case.
    }
  }
  private ws: WebSocket | null = null;
  private detached = false;
  private attachGeneration = 0;
  private outputStream: MediaStream | null = null;
  private canvas: HTMLCanvasElement | null = null;
  /** Container div for Wasm path — h265web.js appends its own canvas inside this. */
  private wasmContainer: HTMLDivElement | null = null;
  /** setInterval handle for the Wasm → 2D canvas relay (Firefox fallback). */
  private wasmRelayInterval: ReturnType<typeof setInterval> | null = null;
  private wcDecoder: WebCodecsDecoder | null = null;
  private wasmDecoder: WasmDecoder | null = null;
  private decoderType: DecoderType | null = null;
  private bytesReceived = 0;
  private startTime = 0;
  /** Frames buffered while async _handleVideoInit() is in progress. */
  private initQueue: ArrayBuffer[] = [];
  private initInProgress = false;
  private visualProbe: VisualArtifactProbe | null = null;
  /** H2 probe: log NAL structure of the first N IDR ACUs. */
  private _idrAcuLogCount = 0;
  private activeCodec: 'h264' | 'h265' | null = null;
  private activeCodecString: string | null = null;
  private activeDescription: Uint8Array | null = null;
  private activeWidth = 0;
  /** Highest PTS fed to the decoder — guards against presenting backward frames. */
  private lastFedPtsUs = -Infinity;
  private activeHeight = 0;
  private activeH265FrameArea = 0;
  private initH265Sps = new Uint8Array(0);
  private initH265Pps = new Uint8Array(0);
  private firstKeyframeTimingSent = false;

  /**
   * @param wasmBaseUrl  Base URL for h265web.js Wasm static files.
   *                     Default '/static/' — must be served by the app.
   * @param opts         Tunables (typically wired from app env at build time):
   *                     - jitterBufferInitialMs / jitterBufferMaxMs: smoothing
   *                       buffer depth (deeper absorbs more burst/late arrival
   *                       at the cost of latency). Default 50 / 200.
   *                     - recoveringFallbackMs: after this long orphaned with no
   *                       re-anchor keyframe, admit P-frames onto the stale
   *                       reference (motion-but-artifact) instead of freezing.
   *                       0 = never freeze (always keep moving). Default 800.
   */
  constructor(
    private readonly wasmBaseUrl = '/static/',
    opts: HybridPlayerAdapterOptions = {},
  ) {
    this.gate = new FrameQualityGate({ recoveringFallbackMs: opts.recoveringFallbackMs });
    this.audioTargetMsOpt = opts.audioBufferTargetMs;
    this.jitter = new JitterBuffer<Uint8Array>({
      initialTargetMs: opts.jitterBufferInitialMs,
      maxTargetMs: opts.jitterBufferMaxMs,
    });
  }

  /**
   * Preload Linux H.265 Wasm assets before a stream's VIDEO_INIT arrives.
   * Do not instantiate h265web here: its worker creates canvas DOM under a
   * player-specific container, which only exists when the actual tile opens.
   */
  static warmWasmDecoder(wasmBaseUrl: string): Promise<void> {
    // The CMS provider can construct its runtime while React is still mounting.
    // Skip speculative warm-up until a body exists; normal playback still builds
    // on demand, so this optimization can never break live view.
    if (typeof document === 'undefined' || !document.body) return Promise.resolve();
    if (this.warmedWasm?.baseUrl !== wasmBaseUrl) {
      const promise = (async (): Promise<void> => {
        if (await detectDecoderType('h265') !== 'wasm') return;
        for (const [file, as] of [['h265web_wasm.wasm', 'fetch'], ['h265web_wasm.js', 'script']] as const) {
          const href = `${wasmBaseUrl}${file}`;
          if (!document.querySelector(`link[rel="preload"][href="${href}"]`)) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = href;
            link.as = as;
            if (file.endsWith('.wasm')) link.type = 'application/wasm';
            document.head.appendChild(link);
          }
        }

        if ((window as { H265webjsPlayer?: unknown }).H265webjsPlayer) return;
        await new Promise<void>((resolve, reject) => {
          const src = `${wasmBaseUrl}h265web.js`;
          const existing = document.querySelector(`script[src="${src}"]`);
          if (existing) { existing.addEventListener('load', () => resolve(), { once: true }); return; }
          const script = document.createElement('script');
          script.src = src;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error(`[HybridPlayerAdapter] Failed to preload ${src}`));
          document.head.appendChild(script);
        });
      })();
      this.warmedWasm = { baseUrl: wasmBaseUrl, promise };
    }

    return this.warmedWasm.promise.then(() => undefined);
  }

  // ── Frame quality gate + jitter buffer (JT/T1078 path) ───────────────────
  private readonly gate: FrameQualityGate;
  private readonly jitter: JitterBuffer<Uint8Array>;
  private gateUnsubscribe: (() => void) | null = null;
  private drainTimer: ReturnType<typeof setInterval> | null = null;
  private adaptTimer: ReturnType<typeof setInterval> | null = null;

  // ── Audio (0x03/0x04) ─────────────────────────────────────────────────────
  /** Created lazily on the first audio message so a video-only page never
   *  constructs an AudioContext (and never triggers Chrome's
   *  "AudioContext was not allowed to start" warning on every page view). */
  private audioSink: AudioSink | null = null;
  private readonly audioTargetMsOpt: number | undefined;
  private audioStateHandler: ((change: PlayerAudioStateChange) => void) | null = null;
  /** Requested audible state, remembered across a lazy sink construction so a
   *  click that lands before the first 0x04 is not lost. */
  private audioEnabledRequested = false;
  private audioVolume = 1;
  /** Audio bytes are counted separately: folding them into `bytesReceived`
   *  would silently add ~65 kbit/s to the tile's video bitrate pill. */
  private audioBytesReceived = 0;
  private audioFramesReceived = 0;
  private audioSampleRate = 0;
  private audioCodecName: string | null = null;
  private lastAudioPtsUs = Number.NEGATIVE_INFINITY;
  private lastAudioFrameDurationUs = 0;
  private audioInitCount = 0;
  private audioOversizedFrames = 0;

  attach(
    uri: string,
    onReady: (stream: MediaStream) => void,
    onError: (err: Error) => void,
    onFrameQuality?: (change: PlayerFrameQualityChange) => void,
    onAudioState?: (change: PlayerAudioStateChange) => void,
  ): void {
    const generation = ++this.attachGeneration;
    this.detached = false;
    this.ws = new WebSocket(uri);
    this.ws.binaryType = 'arraybuffer';
    this.startTime = performance.now();
    this.bytesReceived = 0;
    this.firstKeyframeTimingSent = false;
    this.audioStateHandler = onAudioState ?? null;
    this.audioBytesReceived = 0;
    this.audioFramesReceived = 0;
    this.audioSampleRate = 0;
    this.audioCodecName = null;
    this.lastAudioPtsUs = Number.NEGATIVE_INFINITY;
    this.lastAudioFrameDurationUs = 0;
    this.audioInitCount = 0;
    this.audioOversizedFrames = 0;

    if (onFrameQuality) {
      this.gateUnsubscribe?.();
      this.gateUnsubscribe = this.gate.onChange((change) => {
        try { onFrameQuality(change); } catch (err) {
          console.error('[HybridPlayerAdapter] frame quality listener threw:', err);
        }
      });
    }
    // 60 Hz drain so steady cadence keeps going during network silence.
    this.drainTimer = setInterval(() => this._drainAndDecode(), 16);
    // 1 Hz adapt of jitter target.
    this.adaptTimer = setInterval(() => this.jitter.adapt(), 1000);

    let _dbgFrameCount = 0;
    this.ws.onopen = () => {
      if (this.detached || generation !== this.attachGeneration) return;
      emitLiveTiming('relay_ws_open');
      console.info('[HybridPlayerAdapter] WS open', uri);
    };
    this.ws.onmessage = (ev: MessageEvent<ArrayBuffer>) => {
      if (this.detached || generation !== this.attachGeneration) return;
      const buf = ev.data;
      if (buf.byteLength < 1) return;
      const msgType = new Uint8Array(buf)[0];
      // Split the byte counters at the tag. `bytesReceived` feeds
      // videoBitrateKbps in getStats() and the tile's bitrate pill; a stream
      // with no audio therefore counts byte-for-byte as it did before.
      if (msgType === MSG_AUDIO_INIT || msgType === MSG_AUDIO_FRAME) {
        this.audioBytesReceived += buf.byteLength;
      } else {
        this.bytesReceived += buf.byteLength;
      }

      if (msgType === MSG_VIDEO_INIT) {
        emitLiveTiming('video_init_received');
        console.info('[HybridPlayerAdapter] MSG_VIDEO_INIT received, initInProgress=', this.initInProgress, 'decoderType=', this.decoderType);
        // Only process the first VIDEO_INIT (or if decoder not yet configured).
        // Re-init mid-stream is not yet supported.
        if (!this.initInProgress && !this.decoderType) {
          this.initInProgress = true;
          this._handleVideoInit(buf, onReady, onError, generation).catch((err) => {
            if (this.detached || generation !== this.attachGeneration) return;
            console.error('[HybridPlayerAdapter] init failed:', err);
            onError(err instanceof Error ? err : new Error(String(err)));
          });
        }
      } else if (msgType === MSG_VIDEO_FRAME) {
        if (_dbgFrameCount === 0) emitLiveTiming('first_video_frame_received');
        _dbgFrameCount++;
        if (isH265DebugEnabled() && (_dbgFrameCount <= 5 || _dbgFrameCount % 100 === 0)) {
          console.info('[HybridPlayerAdapter] MSG_VIDEO_FRAME #' + _dbgFrameCount + ' initInProgress=' + this.initInProgress + ' queueLen=' + this.initQueue.length);
        }
        if (!this.firstKeyframeTimingSent && new DataView(buf).getUint8(1) & 0x01) {
          this.firstKeyframeTimingSent = true;
          emitLiveTiming('keyframe_received');
        }
        if (this.initInProgress) {
          // Buffer frames received during async init — flushed after configure().
          this.initQueue.push(buf);
        } else {
          this._handleVideoFrame(buf);
        }
      } else if (msgType === MSG_AUDIO_INIT) {
        this._handleAudioInit(buf);
      } else if (msgType === MSG_AUDIO_FRAME) {
        this._handleAudioFrame(buf);
      }
      // No `else`. An unrecognised tag must stay inert — that is the property
      // that let 0x03/0x04 ship to clients built before they existed.
    };

    this.ws.onerror = (e) => {
      if (this.detached || generation !== this.attachGeneration) return;
      console.error('[HybridPlayerAdapter] WS error', e);
      onError(new Error('[HybridPlayerAdapter] WebSocket error'));
    };
    this.ws.onclose = (e) => {
      if (generation !== this.attachGeneration) return;
      console.warn('[HybridPlayerAdapter] WS closed code=', e.code, 'reason=', e.reason, 'wasClean=', e.wasClean);
      if (!this.detached) {
        onError(new Error(`[HybridPlayerAdapter] WebSocket closed unexpectedly (code=${e.code})`));
      }
    };
  }

  private async _handleVideoInit(
    buf: ArrayBuffer,
    onReady: (stream: MediaStream) => void,
    onError: (err: Error) => void,
    generation: number,
  ): Promise<void> {
    const init = parseVideoInit(buf);
    const codec = init.codec === CODEC_H265 ? 'h265' : 'h264';
    const h265Config = codec === 'h265' ? buildH265DecoderConfig(init.sps, init.pps) : null;
    const codecString =
      codec === 'h264' ? buildH264CodecString(init.sps) : (h265Config?.codecString ?? buildH265CodecString(init.sps));
    const description = codec === 'h265' && h265Config?.description
      ? new Uint8Array(h265Config.description)
      : undefined;
    this.activeCodec = codec;
    this.activeCodecString = codecString;
    this.activeDescription = description ?? null;
    // Relay sends 0×0 in VIDEO_INIT; fall back to the dimensions parsed from
    // the H265 SPS conformance window so the canvas/decoder get the real
    // resolution (and aspect ratio) from the very first frame.
    const resolvedWidth = init.width || h265Config?.width || 0;
    const resolvedHeight = init.height || h265Config?.height || 0;
    this.activeWidth = resolvedWidth;
    this.activeHeight = resolvedHeight;
    this.activeH265FrameArea = h265Config?.frameArea ?? 0;
    this.initH265Sps = codec === 'h265' ? new Uint8Array(init.sps) : new Uint8Array(0);
    this.initH265Pps = codec === 'h265' ? new Uint8Array(init.pps) : new Uint8Array(0);
    console.info(`[HybridPlayerAdapter] codec string: ${codecString}${description ? ' + hvcc' : ''}`);
    this.decoderType = await detectDecoderType(codec, codecString, description);
    if (this.detached || generation !== this.attachGeneration) return;

    // Fail-fast: WASM renderer only supports 8-bit YUV420P. If the H265 stream
    // uses 10-bit (bitDepthLumaMinus8 > 0) and WebCodecs is not available (WASM
    // path was selected), surface a clear error instead of showing a black canvas.
    if (this.decoderType === 'wasm' && codec === 'h265') {
      const spsNal = _findH265NalUnit([init.sps], 33);
      if (spsNal) {
        const spsConfig = _parseH265SpsConfig(spsNal);
        if (spsConfig && spsConfig.bitDepthLumaMinus8 > 0) {
          const bitDepth = spsConfig.bitDepthLumaMinus8 + 8;
          console.warn(`[HybridPlayerAdapter] ${bitDepth}-bit H265 detected on WASM path — not renderable`);
          const err = new Error(
            `Video HD dùng định dạng H.265 ${bitDepth}-bit không được hỗ trợ bởi trình giải mã phần mềm. ` +
            `Vui lòng chuyển về chất lượng SD hoặc dùng trình duyệt Chrome/Edge trên máy có phần cứng hỗ trợ H.265.`,
          );
          (err as Error & { nonRetryable?: boolean }).nonRetryable = true;
          this.initInProgress = false;
          onError(err);
          return;
        }
      }
    }

    // Resolved width/height — VIDEO_INIT is 0×0, prefer SPS-parsed dims; only
    // fall back to a 4:3 placeholder if neither is available (e.g. H264 path).
    const w = resolvedWidth || 640;
    const h = resolvedHeight || 480;
    const fps = init.fps || 25;

    // Reset frame-quality gate + jitter buffer for the new stream and seed
    // the gate's PTS-gap threshold from the encoder's fps.
    this.gate.reset();
    this.gate.setExpectedFrameIntervalMs(1000 / fps);
    this.jitter.reset();
    this.lastFedPtsUs = -Infinity;

    if (this.decoderType === 'webcodecs') {
      // WebCodecs path: create an off-screen canvas; decoder renders into it.
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.style.cssText =
        'position:fixed;top:-2px;left:-2px;width:1px;height:1px;opacity:0;pointer-events:none';
      document.body.appendChild(canvas);
      this.canvas = canvas;

      this.wcDecoder = new WebCodecsDecoder(canvas);
      // Pipe decoder errors into the gate so a rejected bitstream flips us
      // into 'recovering' before the next P-frame poisons the canvas.
      this.wcDecoder.onDecoderError(() => this.gate.notifyDecoderError());
      try {
        this.wcDecoder.configure(codec, resolvedWidth, resolvedHeight, codecString, description);
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      // Visual artifact probe for the WebCodecs render target. The canvas is
      // off-screen but drawImage() readback works regardless of compositor.
      if (isH265DebugEnabled()) {
        this.visualProbe = new VisualArtifactProbe(canvas);
        this.visualProbe.start();
      }
    } else {
      // Wasm path (H.265 fallback on Firefox / Safari Intel / old Android):
      // h265web.js expects a <div> container identified by an id. It creates a
      // new <canvas> element inside that div for its WebGL rendering context.
      // We must capture THAT canvas — not an outer placeholder — for MediaStream.
      try {
        // Two tiles can receive VIDEO_INIT in the same millisecond. h265web
        // resolves its player container by id, so a timestamp alone lets their
        // workers collide and attempt to append canvas into the wrong container.
        const containerId = `vgps-wasm-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const container = document.createElement('div');
        container.id = containerId;
        // Hide off-screen; must remain in document so WebGL context stays alive.
        container.style.cssText =
          'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none';
        document.body.appendChild(container);
        this.wasmContainer = container;

      this.wasmDecoder = new WasmDecoder(containerId);
        await this.wasmDecoder.build({
          wasmBaseUrl: this.wasmBaseUrl,
          width: w,
          height: h,
          fps,
        });
        // initLiveDecoder sends probe_ok to the decoder worker so it initialises
        // its WebGL context. The h265web.js patch defers this asynchronously
        // (up to ~1 s) to ensure the Wasm module has finished loading inside the
        // worker. We MUST await it so that the initQueue flush below does not
        // race — frames posted before probe_ok arrives in the worker would be
        // decoded without a codec context, producing POC errors and fast-forward.
        await this.wasmDecoder.initLiveDecoder(w, h);
      } catch (err) {
        if (this.detached || generation !== this.attachGeneration) return;
        onError(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      if (this.detached || generation !== this.attachGeneration) return;

      // h265web.js creates a <canvas> synchronously inside the container during
      // build(). Use that canvas for captureStream() so we capture Wasm output.
      const wasmCanvas = this.wasmContainer?.querySelector('canvas') as HTMLCanvasElement | null;
      if (!wasmCanvas) {
        onError(new Error('[HybridPlayerAdapter] Wasm decoder did not create a canvas in the container'));
        return;
      }
      this.canvas = wasmCanvas;
    }

    emitLiveTiming('decoder_ready');

    // Start streaming canvas output as MediaStream via a 2D canvas intermediary.
    //
    // captureStream() on a WebGL canvas is unreliable: Chrome only composites
    // visible elements, so an off-screen canvas (-9999px) never produces frames
    // even with requestFrame(). The 2D canvas approach avoids the compositor
    // entirely: drawImage(webglCanvas) does a direct GPU→CPU readback (works even
    // off-screen), then requestFrame() on the 2D captureStream delivers the frame.
    //
    // The WebGL canvas has preserveDrawingBuffer:true (h265web.js patch) so the
    // framebuffer is still valid when drawImage() is called in the callback.
    let stream: MediaStream;
    if (this.wasmDecoder && this.canvas) {
      const webglCanvas = this.canvas;
      const canvas2d = document.createElement('canvas');
      canvas2d.width = webglCanvas.width || w;
      canvas2d.height = webglCanvas.height || h;
      // Keep it hidden but attached to the DOM inside the Wasm container so it is
      // cleaned up automatically when the container is removed on detach().
      canvas2d.style.cssText = 'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;';
      (this.wasmContainer ?? document.body).appendChild(canvas2d);

      const ctx2d = canvas2d.getContext('2d');

      // Generic relay tick: keep canvas2d sized to whatever h265web.js' WebGL
      // canvas currently is (it can change on SD↔HD quality switch), then copy
      // the framebuffer over. This single helper is used by every browser path
      // below — no per-browser drawing logic.
      //
      // Without the resize, drawImage(webglCanvas, 0, 0) copies pixel-for-pixel
      // and you'd only see the top-left region of the new resolution
      // (e.g. SD-sized 640×360 view of an HD 1280×720 frame).
      const relayDraw = (): void => {
        try {
          const sw = webglCanvas.width;
          const sh = webglCanvas.height;
          if (sw > 0 && sh > 0 && (canvas2d.width !== sw || canvas2d.height !== sh)) {
            canvas2d.width = sw;
            canvas2d.height = sh;
          }
          ctx2d!.drawImage(webglCanvas, 0, 0);
        } catch {
          // WebGL canvas may be gone during teardown; ignore.
        }
      };

      // Capability detection (no UA sniffing). Each capability gives us a
      // different driver:
      //   - canvas.captureStream + MediaStreamTrack.requestFrame  → callback-driven
      //     (Chromium, Safari ≥ 15, Edge, Opera, Coc Coc): cheapest, only paints
      //     when the decoder actually produced a frame.
      //   - canvas.captureStream(fps) only                        → interval-driven
      //     (Firefox, older Safari, anything missing requestFrame): paints at
      //     wall-clock fps regardless of decoder activity.
      //   - neither                                               → direct WebGL
      //     captureStream (last-resort, low quality off-screen).
      // Adding support for a new browser/version normally needs no code change;
      // it falls into one of the existing branches automatically.
      const probeStream = ctx2d ? canvas2d.captureStream(0) : null;
      const videoTrackProbe = probeStream?.getVideoTracks()[0] as
        (CanvasCaptureMediaStreamTrack & { requestFrame?: () => void }) | undefined;
      const hasRequestFrame = typeof videoTrackProbe?.requestFrame === 'function';
      probeStream?.getTracks().forEach((track) => track.stop());

      if (ctx2d && hasRequestFrame) {
        stream = canvas2d.captureStream(0);
        const videoTrack = stream.getVideoTracks()[0] as
          CanvasCaptureMediaStreamTrack & { requestFrame: () => void };
        const rf = videoTrack.requestFrame.bind(videoTrack);
        this.wasmDecoder.setVideoRenderCallback(() => {
          relayDraw();
          rf();
        });
        console.info('[HybridPlayerAdapter] 2D-canvas captureStream relay installed (callback-driven)');
      } else if (ctx2d) {
        // h265web.js' video_render_callback may not fire reliably here, so we
        // pace the copy ourselves. captureStream(fps) auto-emits at that rate;
        // preserveDrawingBuffer:true (h265web.js patch) keeps the WebGL
        // framebuffer valid between paints.
        const targetFps = Math.max(1, Math.min(60, Math.round(fps || 25)));
        stream = canvas2d.captureStream(targetFps);
        const intervalMs = Math.max(16, Math.round(1000 / targetFps));
        this.wasmRelayInterval = setInterval(relayDraw, intervalMs);
        console.info(
          `[HybridPlayerAdapter] 2D-canvas captureStream relay installed ` +
          `(interval-driven @${targetFps}fps, every ${intervalMs}ms)`,
        );
      } else {
        stream = webglCanvas.captureStream(Math.max(1, Math.round(fps || 25)));
        console.warn('[HybridPlayerAdapter] 2D ctx unavailable, using direct WebGL captureStream');
      }

      // Visual probe runs on the 2D canvas (already kept in sync with the
      // WebGL output). Gated by the H.265 debug flag so production stays silent.
      if (ctx2d && isH265DebugEnabled()) {
        this.visualProbe = new VisualArtifactProbe(canvas2d);
        this.visualProbe.start();
      }

      // Start the cylopengl render loop. Without this, the ffdecoder worker
      // decodes frames but they are never popped from the Wasm cache and drawn.
      this.wasmDecoder.startRender();
      console.info('[HybridPlayerAdapter] WASM render loop started');
    } else {
      stream = (this.canvas as HTMLCanvasElement).captureStream(fps);
    }

    if (this.detached || generation !== this.attachGeneration) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.outputStream = stream;
    console.info('[HybridPlayerAdapter] onReady stream tracks=', stream.getTracks().length, 'decoderType=', this.decoderType, 'queueLen=', this.initQueue.length);
    emitLiveTiming('media_stream_ready');
    onReady(stream);

    // Discard frames buffered during async WASM init.
    //
    // The queue contains P-frames from BEFORE the first live IDR.  Feeding
    // these stale frames to the WASM decoder corrupts its DPB: even though
    // WasmDecoder.feedNalu drops pre-IDR P-frames, the P-frames AFTER the
    // queued IDR reference GOP positions that are then misaligned with the
    // ongoing live stream, triggering avcodec "Could not find ref with POC N"
    // errors → ffdecoder sets seek_pending=true → drops every frame until the
    // next IDR → ~1-second video freeze.
    //
    // By discarding the queue, the decoder starts clean with needsKeyframe=true
    // and shows video at the next live IDR (≤ gop_size/fps seconds away,
    // typically ≤ 1 second with gop_size=fps=15).
    const discardCount = this.initQueue.length;
    this.initQueue = [];
    this.initInProgress = false;
    console.info('[HybridPlayerAdapter] discarded', discardCount, 'stale queued frames — waiting for next live IDR');
  }

  private _maybeUpgradeH265ConfigFromKeyframe(nalu: Uint8Array): void {
    if (
      this.decoderType !== 'webcodecs' ||
      this.activeCodec !== 'h265' ||
      !this.wcDecoder
    ) {
      return;
    }

    const h265Config = buildH265DecoderConfigFromSources(nalu, this.initH265Sps, this.initH265Pps);
    if (!h265Config.description) return;

    if (this.activeH265FrameArea > 0 && h265Config.frameArea > 0 && h265Config.frameArea < this.activeH265FrameArea) {
      if (isH265DebugEnabled()) {
        console.info(
          `[HybridPlayerAdapter] ignoring smaller H.265 live config: ` +
          `${h265Config.codecString} area=${h265Config.frameArea} < active=${this.activeH265FrameArea}`,
        );
      }
      return;
    }

    const description = new Uint8Array(h265Config.description);
    if (
      this.activeCodecString === h265Config.codecString &&
      _equalUint8Arrays(this.activeDescription, description)
    ) {
      return;
    }

    try {
      console.info(
        `[HybridPlayerAdapter] reconfiguring H.265 from live keyframe: ` +
        `${this.activeCodecString ?? 'unset'} -> ${h265Config.codecString} + hvcc`,
      );
      this.wcDecoder.configure(
        'h265',
        this.activeWidth,
        this.activeHeight,
        h265Config.codecString,
        description,
      );
      this.activeCodecString = h265Config.codecString;
      this.activeDescription = description;
      this.activeH265FrameArea = h265Config.frameArea;
    } catch (err) {
      console.warn('[HybridPlayerAdapter] failed to upgrade H.265 config from live keyframe:', err);
    }
  }

  // ── Audio path (0x03 / 0x04) ──────────────────────────────────────────────

  /**
   * Build the audio sink on demand.
   *
   * Called from the first audio message (so a video-only page never builds an
   * AudioContext) and from `setAudioEnabled(true)` (so a click that lands
   * before the first 0x04 still resumes the context inside its own gesture).
   */
  private _ensureAudioSink(): AudioSink {
    if (this.audioSink) return this.audioSink;
    const generation = this.attachGeneration;
    const sink = new AudioSink((state) => {
      // The attachGeneration guard applies to audio callbacks too: a sink from
      // a torn-down attach must never drive the current tile's UI.
      if (this.detached || generation !== this.attachGeneration) return;
      this.audioStateHandler?.({
        enabled: state.enabled,
        contextRunning: state.contextRunning,
        sampleRate: state.announcedSampleRate,
        codec: state.announcedCodec,
        receivingAudio: state.receivingAudio,
        unavailableReason: state.unavailableReason,
      });
    }, this.audioTargetMsOpt);
    this.audioSink = sink;
    sink.setVolume(this.audioVolume);
    // Deliberately does NOT apply `audioEnabledRequested` here. When the click
    // arrives first, `setAudioEnabled` calls this and then enables the sink
    // itself; doing it in both places enabled it twice per click. When audio
    // arrives first, `audioEnabledRequested` is false and there is nothing to
    // apply. The one case this must still cover — enable requested, sink later
    // rebuilt — cannot happen, because the sink is only rebuilt by detach(),
    // which clears the request.
    return sink;
  }

  private _handleAudioInit(buf: ArrayBuffer): void {
    try {
      const init = parseAudioInit(buf);
      if (!init) {
        console.warn('[HybridPlayerAdapter] MSG_AUDIO_INIT shorter than 8 bytes — ignored');
        return;
      }
      this.audioInitCount++;
      const codecName = g711CodecName(init.codec);
      const changed = init.sampleRate !== this.audioSampleRate || codecName !== this.audioCodecName;
      this.audioSampleRate = init.sampleRate;
      this.audioCodecName = codecName;

      if (changed) {
        console.info(
          `[HybridPlayerAdapter] MSG_AUDIO_INIT ${codecName ?? `codec=${init.codec}`} ` +
            `${init.sampleRate}Hz ch=${init.channels} bits=${init.bitsPerSample}`,
        );
      }
      // The relay re-sends 0x03 every ~500 audio packets (≈20 s) and replays it
      // to every late subscriber before any frame. `configure()` is idempotent:
      // reconfiguring on each one would tear the audio node down three times a
      // minute.
      this._ensureAudioSink().configure(init.codec, init.sampleRate, init.channels);
    } catch (err) {
      // Never let the audio path throw out of ws.onmessage — that would kill
      // the socket handler and take video down with it.
      console.error('[HybridPlayerAdapter] MSG_AUDIO_INIT handling failed:', err);
    }
  }

  private _handleAudioFrame(buf: ArrayBuffer): void {
    try {
      // 0  u8   tag (0x04)
      // 1  u8   flags — reserved, always 0. NOT the video flag semantics.
      // 2  i64  pts, microseconds, BIG-ENDIAN
      // 10 u32  payload length, BIG-ENDIAN
      // 14 ..   one complete G.711 frame (never fragmented)
      if (buf.byteLength < 14) {
        this.audioOversizedFrames++;
        return;
      }
      const v = new DataView(buf);
      const ptsUs = Number(v.getBigInt64(2, false));
      const dataLen = v.getUint32(10, false);

      // Bounds check BEFORE the view. `new Uint8Array(buf, 14, len)` throws in
      // the browser where Node's `subarray` silently clamps, and a throw inside
      // ws.onmessage kills the handler for the rest of the session. The video
      // path has the same latent hole; do not inherit it here.
      if (14 + dataLen > buf.byteLength) {
        this.audioOversizedFrames++;
        if (this.audioOversizedFrames <= 3) {
          console.warn(
            `[HybridPlayerAdapter] MSG_AUDIO_FRAME declares ${dataLen} bytes but the ` +
              `message holds ${buf.byteLength - 14} — dropped`,
          );
        }
        return;
      }
      if (dataLen === 0) return; // the relay drops empties; belt and braces

      const sink = this._ensureAudioSink();
      const payload = new Uint8Array(buf, 14, dataLen);
      this.audioFramesReceived++;

      // G.711 is one byte per sample, so payload length is the frame duration.
      const rate = this.audioSampleRate > 0 ? this.audioSampleRate : 8000;
      const frameDurationUs = (dataLen / rate) * 1e6;

      let silenceSamples = 0;
      if (this.lastAudioPtsUs !== Number.NEGATIVE_INFINITY) {
        const delta = ptsUs - this.lastAudioPtsUs;
        if (delta < 0) {
          if (delta > -AUDIO_DISCONTINUITY_US) {
            // Small backward step = a stale/reordered frame. Drop it rather
            // than let it push the ring backwards.
            return;
          }
          // A large backward jump is a device reboot rebasing PTS toward 0.
          // Follow it, or the audio queue wedges forever.
          console.info('[HybridPlayerAdapter] audio PTS rebase detected — flushing ring');
          sink.flush();
        } else if (delta >= AUDIO_DISCONTINUITY_US) {
          console.info('[HybridPlayerAdapter] audio PTS gap > 3 s — flushing ring');
          sink.flush();
        } else {
          // A real, measured gap becomes measured silence, so speech is not
          // time-compressed. A PTS gap cannot distinguish "device didn't send"
          // from "network lost it" from "relay dropped it for a slow consumer"
          // — all three want the same treatment.
          const gapUs = delta - this.lastAudioFrameDurationUs;
          if (gapUs > this.lastAudioFrameDurationUs / 2) {
            const cappedUs = Math.min(gapUs, AUDIO_MAX_SILENCE_US);
            silenceSamples = Math.round((cappedUs / 1e6) * rate);
          }
        }
      }

      this.lastAudioPtsUs = ptsUs;
      this.lastAudioFrameDurationUs = frameDurationUs;
      sink.pushFrame(payload, ptsUs, silenceSamples);
    } catch (err) {
      console.error('[HybridPlayerAdapter] MSG_AUDIO_FRAME handling failed:', err);
    }
  }

  /**
   * Make this stream the page's one audible stream, or silence it.
   *
   * Reachable synchronously from a click handler: the AudioContext is created
   * and `resume()` called before anything is awaited (see `AudioSink`).
   */
  setAudioEnabled(enabled: boolean): void {
    this.audioEnabledRequested = enabled;
    if (!enabled) {
      this.audioSink?.setEnabled(false);
      return;
    }
    // Build the sink now if the click beat the first audio message, so the
    // gesture is not wasted.
    this._ensureAudioSink().setEnabled(true);
  }

  setAudioVolume(volume: number): void {
    this.audioVolume = Math.max(0, Math.min(1, volume));
    this.audioSink?.setVolume(this.audioVolume);
  }

  private _handleVideoFrame(buf: ArrayBuffer): void {
    if (!this.decoderType) return;

    const v = new DataView(buf);
    // Byte 0: type (0x02)
    // Byte 1: flags (bit0=keyframe, bit1=codec)
    // Bytes 2-9: pts_us (int64 BE)
    // Bytes 10-13: data_len (uint32 BE)
    // Bytes 14+: nalu data
    const flags = v.getUint8(1);
    const isKeyframe = (flags & 0x01) === 1;
    const ptsUs = Number(v.getBigInt64(2, false));
    const dataLen = v.getUint32(10, false);
    const nalu = new Uint8Array(buf, 14, dataLen);

    // H2 probe: log the Annex B NAL types present in the first 10 IDR ACUs.
    // Confirms whether VPS(32)+SPS(33)+PPS(34) are prepended before every IDR.
    if (isKeyframe && this._idrAcuLogCount < 10 && isH265DebugEnabled()) {
      this._idrAcuLogCount++;
      const types = _listAnnexBNalTypes(nalu);
      console.info(`[h265-event] IDR ACU #${this._idrAcuLogCount} nal_types=[${types.join(',')}]`);
    }

    if (isKeyframe) {
      this._maybeUpgradeH265ConfigFromKeyframe(nalu);
    }

    // Push into the jitter buffer; the gate-aware drain feeds the decoder.
    this.jitter.push(nalu, ptsUs, isKeyframe);
    this._drainAndDecode();
  }

  /**
   * Drain frames aged past the jitter buffer target, gate each one, feed
   * admitted frames into the active decoder. Called from the 60 Hz drainTimer
   * and from `_handleVideoFrame` for immediate-drain steady cadence.
   */
  private _drainAndDecode(): void {
    if (!this.decoderType) return;
    const drained = this.jitter.drain();
    for (const f of drained) {
      // Monotonic-PTS guard: never present a frame older than one already shown,
      // so a stale/out-of-order push can't make the camera's burned-in OSD clock
      // jump backwards. EXCEPT a large backward jump (> BACKWARD_REBASE_US) is a
      // stream rebase (camera restart / PTS base change) — follow it instead of
      // dropping every frame forever (which would freeze video until VIDEO_INIT).
      if (
        f.ptsUs < this.lastFedPtsUs &&
        f.ptsUs >= this.lastFedPtsUs - BACKWARD_REBASE_US
      ) {
        continue; // small backward = stale → drop, OSD stays monotonic
      }
      // PLAN-live-field-r2-round2-player.md §3 A: the monotonic guard above
      // runs FIRST — a spliced stale IDR (REVIEW-live-field-r2-round1.md §2)
      // never reaches the gate at all, exactly as measured on real captures.
      // Only THEN does the admission gate get a say: a genuine post-hole
      // delta is dropped instead of being decoded into corrupted pixels.
      // `lastFedPtsUs` updates only on frames actually admitted — it means
      // "fed to the decoder", and both this guard and audioVideoSkewMs read
      // it as such; advancing it on a gate-dropped frame would make both
      // think video got further than it did.
      if (!this.gate.admit(f.ptsUs, f.isKeyframe)) continue;
      this.lastFedPtsUs = f.ptsUs;
      if (this.decoderType === 'webcodecs') {
        this.wcDecoder?.decode(f.frame, f.isKeyframe, f.ptsUs);
      } else {
        this.wasmDecoder?.feedNalu(f.frame, f.isKeyframe, f.ptsUs);
      }
    }
  }

  detach(): void {
    this.detached = true;
    this.attachGeneration++;
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
    }
    this.ws = null;

    if (this.wasmRelayInterval !== null) {
      clearInterval(this.wasmRelayInterval);
      this.wasmRelayInterval = null;
    }
    if (this.drainTimer !== null) { clearInterval(this.drainTimer); this.drainTimer = null; }
    if (this.adaptTimer !== null) { clearInterval(this.adaptTimer); this.adaptTimer = null; }
    if (this.gateUnsubscribe) { this.gateUnsubscribe(); this.gateUnsubscribe = null; }
    this.gate.reset();
    this.jitter.reset();
    this.lastFedPtsUs = -Infinity;

    // Tear the audio graph down alongside the video decoders. Leaking
    // AudioWorkletNodes is how Chrome's per-document AudioContext cap gets
    // reached after a few dozen tile open/close cycles.
    this.audioSink?.destroy();
    this.audioSink = null;
    this.audioStateHandler = null;
    this.audioEnabledRequested = false;
    this.lastAudioPtsUs = Number.NEGATIVE_INFINITY;
    this.lastAudioFrameDurationUs = 0;

    this.visualProbe?.stop();
    this.visualProbe = null;
    this.outputStream?.getTracks().forEach((track) => track.stop());
    this.outputStream = null;

    this.initQueue = [];
    this.initInProgress = false;
    this.decoderType = null;
    this.activeCodec = null;
    this.activeCodecString = null;
    this.activeDescription = null;
    this.activeWidth = 0;
    this.activeHeight = 0;
    this.activeH265FrameArea = 0;
    this.initH265Sps = new Uint8Array(0);
    this.initH265Pps = new Uint8Array(0);

    this.wcDecoder?.destroy();
    this.wcDecoder = null;

    const disposedWasmDecoder = this.wasmDecoder !== null;
    this.wasmDecoder?.destroy();
    this.wasmDecoder = null;

    const gl = this.canvas?.getContext('webgl2') ?? this.canvas?.getContext('webgl');
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    this.canvas?.remove();
    this.canvas = null;

    this.wasmContainer?.remove();
    this.wasmContainer = null;

    if (disposedWasmDecoder) HybridPlayerAdapter.noteWasmDecoderDisposed();

    this.decoderType = null;
  }

  getStats(): PlayerAdapterStats {
    const elapsed = (performance.now() - this.startTime) / 1000;
    const videoBitrateKbps =
      elapsed > 0 ? (this.bytesReceived * 8) / elapsed / 1000 : 0;

    const s = this.wcDecoder?.getStats() ?? this.wasmDecoder?.getStats() ?? { decodedFrames: 0, droppedFrames: 0 };
    const audio = this.audioSink?.getStats() ?? null;
    // Audio PTS minus the PTS last fed to the video decoder. Excludes only the
    // opaque captureStream → <video> display leg, which cannot be observed from
    // JS with any precision. v1 ships free-running by design (§7.1); this is
    // the number that makes the eventual sync fix a one-constant change instead
    // of a fresh investigation.
    const audioVideoSkewMs =
      audio && audio.outputPtsUs > 0 && Number.isFinite(this.lastFedPtsUs)
        ? (audio.outputPtsUs - this.lastFedPtsUs) / 1000
        : undefined;
    return {
      videoBitrateKbps,
      decodedFrames: s.decodedFrames,
      droppedFrames: s.droppedFrames,
      // Extra fields for Stats panel
      bytesReceived: this.bytesReceived,
      frameWidth: 'frameWidth' in s ? (s.frameWidth as number | undefined) : undefined,
      frameHeight: 'frameHeight' in s ? (s.frameHeight as number | undefined) : undefined,
      framesPerSecond: 'framesPerSecond' in s ? (s.framesPerSecond as number | undefined) : undefined,
      framesDropped: s.droppedFrames,
      decoderImplementation: this.decoderType === 'webcodecs' ? 'WebCodecs' : this.decoderType === 'wasm' ? 'Wasm' : undefined,
      streamCodec: this.activeCodec ?? undefined,
      decoderCodec: this.activeCodecString ?? undefined,
      // PLAN-live-field-r2-round2-player.md §3 F. Not on the `PlayerAdapterStats`
      // interface (that lives in livekit_web — out of scope this round); an
      // `as` cast bypasses the excess-property check, so the bench (plain JS,
      // no interface) can still read these while typed callers are unaffected.
      ...this.gate.getGateStats(),
      jitterOverflowTrims: this.jitter.overflowTrims,
      jitterOverflowOldestDropFallback: this.jitter.overflowOldestDropFallback,
      // Audio. `audioSampleRate` is the rate announced by MSG_AUDIO_INIT, not
      // the one advertised in session_bootstrap — the two are independent and
      // nothing anywhere compares them (§7.2), so this is the value that
      // explains "everything plays at half speed".
      audioSampleRate: this.audioSampleRate || undefined,
      audioCodec: this.audioCodecName ?? undefined,
      audioFramesReceived: this.audioFramesReceived,
      audioBytesReceived: this.audioBytesReceived,
      audioRingOccupancyMs: audio?.ringOccupancyMs,
      audioRingOccupancyEmaMs: audio?.ringOccupancyEmaMs,
      audioTargetMs: audio?.targetMs,
      audioUnderruns: audio?.underruns,
      audioOverruns: audio?.overruns,
      audioDriftPpm: audio?.driftPpm,
      audioVideoSkewMs,
      audioEnabled: audio?.enabled ?? false,
      audioContextRunning: audio?.contextRunning ?? false,
    } as PlayerAdapterStats;
  }
}
